import { Logger } from "../utils/logger";
import { callCounters } from "../utils/call-diagnostics";

// An unanswered offer is resent this many times, then dropped until the other side offers again
const MAX_OFFER_RESENDS = 3;
// A 'disconnected' ICE state often recovers by itself: ICE is restarted only if it lasts this long
const ICE_RESTART_DELAY_MS = 3000;

export class Peer extends EventTarget {
  private connectionId: string | null;
  private polite: boolean;
  private config?: RTCConfiguration;
  private pc: RTCPeerConnection | null;
  private makingOffer: boolean;
  private waitingAnswer: boolean;
  private ignoreOffer: boolean;
  private srdAnswerPending: boolean;
  private interval: number;
  private resends: number = 0;
  // The headset ignored our last offer: no new offer until it sends one (after a reconnection it answers none)
  private offerAbandoned: boolean = false;
  private iceRestartTimer: ReturnType<typeof setTimeout> | null = null;
  firstNegotiationEnd: boolean = false;

  constructor(
    connectionId: string,
    polite: boolean,
    config?: RTCConfiguration | string,
    resendIntervalMsec: number = 5000
  ) {
    super();
    this.connectionId = connectionId;
    this.polite = polite;
    if(typeof config === 'string') {
      this.config = Peer.convertStringToRTCConfiguration(config);
    } else {
      this.config = config;
    }
    this.pc = new RTCPeerConnection(this.config);
    callCounters.peers++;
    this.makingOffer = false;
    this.waitingAnswer = false;
    this.ignoreOffer = false;
    this.srdAnswerPending = false;
    this.interval = resendIntervalMsec;

    this.pc.ontrack = (e: RTCTrackEvent) => {
      this.log(`ontrack:${e}`);
      this.dispatchEvent(new CustomEvent('trackevent', { detail: e }));
    };

    this.pc.ondatachannel = (e: RTCDataChannelEvent) => {
      this.log(`ondatachannel:${e}`);
      this.dispatchEvent(new CustomEvent('adddatachannel', { detail: e }));
    };

    this.pc.onicecandidate = ({ candidate }: RTCPeerConnectionIceEvent) => {
      this.log(`send candidate:${candidate}`);
      if (candidate == null) {
        return;
      }
      this.dispatchEvent(new CustomEvent('sendcandidate', { detail: { connectionId: this.connectionId, candidate: candidate.candidate, sdpMLineIndex: candidate.sdpMLineIndex, sdpMid: candidate.sdpMid } }));
    };

    this.pc.onnegotiationneeded = this._onNegotiation.bind(this);

    this.pc.onsignalingstatechange = () => {
      this.log(`signalingState changed:${this.pc!.signalingState}`);
    };

    this.pc.oniceconnectionstatechange = () => {
      this.log(`iceConnectionState changed:${this.pc!.iceConnectionState}`);
      // if (this.pc!.iceConnectionState === 'disconnected' || this.pc!.iceConnectionState === 'failed') {
      //   this.dispatchEvent(new Event('disconnect'));
      // }

      if (this.pc!.iceConnectionState === 'disconnected') {
        this.scheduleIceRestart();
      } else if (this.pc!.iceConnectionState === 'failed') {
        console.error('Rinegoziazione ICE fallita dopo restartIce.');
        // Azione correttiva: chiudere e ricreare la connessione
        this.dispatchEvent(new Event('disconnect'));
      } else if (this.pc!.iceConnectionState === 'connected') {
        console.log(`iceConnectionState changed:${this.pc!.iceConnectionState}`);
      }
    };

    this.pc.onicegatheringstatechange = () => {
      this.log(`iceGatheringState changed:${this.pc!.iceGatheringState}'`);
    };

    this.loopResendOffer();
  }

  private scheduleIceRestart(): void {
    if (this.iceRestartTimer) {
      return;
    }
    this.iceRestartTimer = setTimeout(() => {
      this.iceRestartTimer = null;
      if (this.pc?.iceConnectionState === 'disconnected') {
        console.warn('Disconnessione rilevata, tentativo di restart ICE...');
        this.pc.restartIce();
      }
    }, ICE_RESTART_DELAY_MS);
  }

  public getPeerConnection() {
    return this.pc;
  }

  private log = (str: string): void => {
    Logger.log(`[${this.polite ? 'POLITE' : 'IMPOLITE'}] ${str}`);
  };

  private warn = (str: string): void => {
    Logger.warn(`[${this.polite ? 'POLITE' : 'IMPOLITE'}] ${str}`);
  };

  private assert_equals = (a: any, b: any, msg: string): void => {
    if (a === b) {
      return;
    }
    throw new Error(`${msg} expected ${b} but got ${a}`);
  };

  private sleep = (msec: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, msec));
  };

  private async _onNegotiation(): Promise<void> {
    try {
      if (this.offerAbandoned) {
        this.log('negotiationneeded postponed: the last offer was never answered');
        return;
      }
      this.log(`SLD due to negotiationneeded`);
      this.assert_equals(this.pc!.signalingState, 'stable', 'negotiationneeded always fires in stable state');
      this.assert_equals(this.makingOffer, false, 'negotiationneeded not already in progress');
      this.makingOffer = true;
      let offer = await this.pc!.createOffer();
      let equal = this.checkEqualSdp(offer);

      if (equal) {
        this.log('negotiationneeded SLD is a no-op');
        this.makingOffer = false;
        return;
      }
      await this.pc!.setLocalDescription(offer);
      // await this.pc!.setLocalDescription();
      this.assert_equals(this.pc!.signalingState, 'have-local-offer', 'negotiationneeded not racing with onmessage');
      this.assert_equals(this.pc!.localDescription!.type, 'offer', 'negotiationneeded SLD worked');
      this.waitingAnswer = true;
      this.resends = 0;
      callCounters.offersSent++;
      this.dispatchEvent(new CustomEvent('sendoffer', { detail: { connectionId: this.connectionId, sdp: this.pc!.localDescription!.sdp } }));
    } catch (e) {
      this.log(e as string);
    } finally {
      this.makingOffer = false;
    }
  }

  private checkEqualSdp(offer: RTCSessionDescriptionInit) {
    const localDesc = this.pc!.localDescription;
    // Nothing negotiated yet: this is the first offer, never a no-op
    if (!localDesc) {
      return false;
    }
    let equal = true;
    if (localDesc && offer?.sdp) {
      const ourLines = localDesc.sdp.split('\n');
      const theirLines = offer?.sdp.split('\n');
      if (ourLines.length === theirLines.length) {

        for (let i = 0; i < ourLines.length; i++) {
          if (i === 1) { // o= line
            continue;
          }
          if (ourLines[i] !== theirLines[i]) {
            equal = false;
          }
        }
      } else {
        equal = false;
      }
    }
    return equal;
  }

  private async loopResendOffer(): Promise<void> {
    while (this.connectionId) {
      if (this.pc && this.waitingAnswer) {
        if (this.resends >= MAX_OFFER_RESENDS) {
          await this.abandonOffer();
        } else {
          this.resends++;
          callCounters.offerResends++;
          this.dispatchEvent(new CustomEvent('sendoffer', { detail: { connectionId: this.connectionId, sdp: this.pc.localDescription!.sdp } }));
        }
      }
      await this.sleep(this.interval);
    }
  }

  /** Back to the last negotiated state, so the media keeps flowing and the next offer from the headset is accepted. */
  private async abandonOffer(): Promise<void> {
    this.waitingAnswer = false;
    this.offerAbandoned = true;
    callCounters.offersUnanswered++;
    console.warn(`[connessione] proposta senza risposta dopo ${MAX_OFFER_RESENDS} invii: annullata`);
    try {
      if (this.pc && this.pc.signalingState === 'have-local-offer') {
        await this.pc.setLocalDescription({ type: 'rollback' });
      }
    } catch (e) {
      this.warn(`rollback failed: ${e}`);
    }
    this.dispatchEvent(new CustomEvent('offerunanswered', { detail: { connectionId: this.connectionId } }));
  }

  public close(): void {
    this.connectionId = null;
    if (this.iceRestartTimer) {
      clearTimeout(this.iceRestartTimer);
      this.iceRestartTimer = null;
    }
    if (this.pc) {
      this.pc.close();
      this.pc = null;
      callCounters.peers--;
    }
  }

  public getTransceivers(connectionId: string): RTCRtpTransceiver[] | null {
    if (this.connectionId !== connectionId) {
      return null;
    }
    return this.pc!.getTransceivers();
  }

  public addTrack(connectionId: string, track: MediaStreamTrack): RTCRtpSender | null {
    if (this.connectionId !== connectionId) {
      return null;
    }
    return this.pc!.addTrack(track);
  }

  public removeTrack(connectionId: string, sender: RTCRtpSender): void {
    if (this.connectionId !== connectionId) {
      return;
    }
    this.pc!.removeTrack(sender);
  }

  public addTransceiver(connectionId: string, trackOrKind: MediaStreamTrack | string, init?: RTCRtpTransceiverInit): RTCRtpTransceiver | null {
    if (this.connectionId !== connectionId) {
      return null;
    }
    return this.pc!.addTransceiver(trackOrKind, init);
  }

  public createDataChannel(connectionId: string, label: string): RTCDataChannel | null {
    if (this.connectionId !== connectionId) {
      return null;
    }
    return this.pc!.createDataChannel(label);
  }

  public async getStats(connectionId: string): Promise<RTCStatsReport | null> {
    if (this.connectionId !== connectionId) {
      return null;
    }
    return await this.pc!.getStats();
  }

  public async onGotDescription(connectionId: string, description: RTCSessionDescriptionInit): Promise<void> {
    if (this.connectionId !== connectionId) {
      return;
    }

    const isStable = this.pc!.signalingState === 'stable' || (this.pc!.signalingState === 'have-local-offer' && this.srdAnswerPending);
    this.ignoreOffer = (description.type === 'offer' && !this.polite && (this.makingOffer || !isStable)) || (description.type === 'answer' && this.srdAnswerPending);

    if (this.ignoreOffer) {
      this.log(`glare - ignoring offer`);
      return;
    }

    this.waitingAnswer = false;
    if (description.type === 'offer') {
      this.offerAbandoned = false;
    }
    this.srdAnswerPending = description.type === 'answer';
    this.log(`SRD(${description.type})`);
    await this.pc!.setRemoteDescription(description);
    this.srdAnswerPending = false;

    if (description.type === 'offer') {
      this.dispatchEvent(new CustomEvent('ongotoffer', { detail: { connectionId: this.connectionId } }));

      this.assert_equals(this.pc!.signalingState, 'have-remote-offer', 'Remote offer');
      this.assert_equals(this.pc!.remoteDescription!.type, 'offer', 'SRD worked');
      this.log('SLD to get back to stable');
      await this.pc!.setLocalDescription();
      this.assert_equals(this.pc!.signalingState, 'stable', 'onmessage not racing with negotiationneeded');
      this.assert_equals(this.pc!.localDescription!.type, 'answer', 'onmessage SLD worked');
      callCounters.answersSent++;
      this.dispatchEvent(new CustomEvent('sendanswer', { detail: { connectionId: this.connectionId, sdp: this.pc!.localDescription!.sdp } }));
      
    } else {
      this.dispatchEvent(new CustomEvent('ongotanswer', { detail: { connectionId: this.connectionId } }));

      this.assert_equals(this.pc!.remoteDescription!.type, 'answer', 'Answer was set');
      this.assert_equals(this.pc!.signalingState, 'stable', 'answered');
      this.pc!.dispatchEvent(new Event('negotiated'));
    }

    if(this.firstNegotiationEnd == false) {
      this.firstNegotiationEnd = true;
      this.dispatchEvent(new CustomEvent('firstnegotiationend', { detail: { connectionId: this.connectionId } }));
    }
  }

  public async onGotCandidate(connectionId: string, candidate: RTCIceCandidateInit): Promise<void> {
    if (this.connectionId !== connectionId) {
      return;
    }

    try {
      await this.pc!.addIceCandidate(candidate);
    } catch (e) {
      if (this.pc && !this.ignoreOffer) {
        this.warn(`${this.pc} this candidate can't accept current signaling state ${this.pc.signalingState}.`);
      }
    }
  }

  static convertStringToRTCConfiguration(config: string): RTCConfiguration {
    const symmetricKey = 'as123asd456sdf2345';
    const b64 = Peer.decode(config, symmetricKey);
    const json = atob(b64);
    return JSON.parse(json);
  }

  static convertRTCConfigurationToString(config: RTCConfiguration): string {
    const symmetricKey = 'as123asd456sdf2345';
    const json = JSON.stringify(config);
    const b64 = btoa(json);
    return Peer.encode(b64, symmetricKey);
  }

  static decode(encoded: string, symmetricKey: string): string {
    let key = symmetricKey;
    let result = '';
    for (let i = 0; i < encoded.length; i++) {
      result += String.fromCharCode(encoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
  }

  static encode(decoded: string, symmetricKey: string): string {
    let key = symmetricKey;
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
  }

}
